import { useState, useCallback, useMemo } from 'react';
import { AdvancedFilterConfig } from '../../types/filter';

/**
 * Estado de la tabla que se sincroniza con queries
 */
export interface TableQueryState {
  page: number;
  pageSize: number;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
  filters: AdvancedFilterConfig[];
  search?: string;
}

/**
 * Acciones para modificar el estado de la tabla
 */
export interface TableQueryActions {
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  setSort: (field: string | undefined, direction?: 'asc' | 'desc') => void;
  setFilters: (filters: AdvancedFilterConfig[]) => void;
  addFilter: (filter: AdvancedFilterConfig) => void;
  removeFilter: (filterId: string) => void;
  clearFilters: () => void;
  setSearch: (search: string) => void;
  reset: () => void;
}

/**
 * Opciones iniciales para el estado de la tabla
 */
export interface UseTableQueryStateOptions {
  initialPage?: number;
  initialPageSize?: number;
  initialSortField?: string;
  initialSortDirection?: 'asc' | 'desc';
  initialFilters?: AdvancedFilterConfig[];
  initialSearch?: string;
}

/**
 * Hook para gestionar el estado de la tabla que se sincroniza con TanStack Query
 * Maneja paginación, ordenamiento, filtros y búsqueda
 */
export function useTableQueryState(
  options: UseTableQueryStateOptions = {}
): TableQueryState & TableQueryActions {
  const {
    initialPage = 1,
    initialPageSize = 10,
    initialSortField,
    initialSortDirection = 'asc',
    initialFilters = [],
    initialSearch = '',
  } = options;

  const [page, setPageInternal] = useState(initialPage);
  const [pageSize, setPageSizeInternal] = useState(initialPageSize);
  const [sortField, setSortField] = useState<string | undefined>(initialSortField);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(initialSortDirection);
  const [filters, setFiltersInternal] = useState<AdvancedFilterConfig[]>(initialFilters);
  const [search, setSearchInternal] = useState(initialSearch);

  // Setters con reset de página automático cuando cambian filtros/ordenamiento
  const setPage = useCallback((newPage: number) => {
    setPageInternal(newPage);
  }, []);

  const setPageSize = useCallback((newPageSize: number) => {
    setPageSizeInternal(newPageSize);
    setPageInternal(1); // Reset a primera página
  }, []);

  const setSort = useCallback((field: string | undefined, direction: 'asc' | 'desc' = 'asc') => {
    setSortField(field);
    setSortDirection(direction);
    setPageInternal(1); // Reset a primera página
  }, []);

  const setFilters = useCallback((newFilters: AdvancedFilterConfig[]) => {
    setFiltersInternal(newFilters);
    setPageInternal(1); // Reset a primera página
  }, []);

  const addFilter = useCallback((filter: AdvancedFilterConfig) => {
    setFiltersInternal((prev) => [...prev, filter]);
    setPageInternal(1);
  }, []);

  const removeFilter = useCallback((filterId: string) => {
    setFiltersInternal((prev) => prev.filter((f) => f.id !== filterId));
    setPageInternal(1);
  }, []);

  const clearFilters = useCallback(() => {
    setFiltersInternal([]);
    setPageInternal(1);
  }, []);

  const setSearch = useCallback((newSearch: string) => {
    setSearchInternal(newSearch);
    setPageInternal(1);
  }, []);

  const reset = useCallback(() => {
    setPageInternal(initialPage);
    setPageSizeInternal(initialPageSize);
    setSortField(initialSortField);
    setSortDirection(initialSortDirection);
    setFiltersInternal(initialFilters);
    setSearchInternal(initialSearch);
  }, [
    initialPage,
    initialPageSize,
    initialSortField,
    initialSortDirection,
    initialFilters,
    initialSearch,
  ]);

  return {
    // Estado
    page,
    pageSize,
    sortField,
    sortDirection,
    filters,
    search,

    // Acciones
    setPage,
    setPageSize,
    setSort,
    setFilters,
    addFilter,
    removeFilter,
    clearFilters,
    setSearch,
    reset,
  };
}

/**
 * Hook para convertir el estado de la tabla en columnas con estado de sort/filter
 */
export function useColumnsWithQueryState<T extends { id: string }>(
  columns: T[],
  state: Pick<TableQueryState, 'sortField' | 'sortDirection' | 'filters'>
) {
  return useMemo(() => {
    return columns.map((column) => {
      const isCurrentSort = state.sortField === column.id;
      const hasActiveFilter = state.filters.some((f) => f.columnId === column.id);

      return {
        ...column,
        sortDirection: isCurrentSort ? state.sortDirection : null,
        filterActive: hasActiveFilter,
      };
    });
  }, [columns, state.sortField, state.sortDirection, state.filters]);
}
